import React from "react";
import {Alert, Box, Button, Chip, FormControlLabel, Paper, Stack, Switch, Typography} from "@mui/material";
import {formatReais} from "../../../shared/utils/formatters";
import {dataBR, efeitoNoAcesso, vigente} from "../assinaturasUtils";

const celula = {px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider', verticalAlign: 'top', fontSize: 14};

// Funcionalidades liberadas agora, pela situação: somente consulta deixa só as de leitura (BBF-144).
const liberadas = (funcionalidades, nivel) => {
    if (nivel === 'nenhum') return 0;
    if (nivel === 'leitura') return funcionalidades.filter((item) => item.tipo === 'LEITURA').length;
    return funcionalidades.length;
};

/*
 * Direito e opcionais (protótipo v2): o direito é a soma dos módulos incluídos na versão e dos opcionais
 * contratados. Contratar um opcional vale na hora, e o valor entra na fatura seguinte, sem pró-rata; tirar fica para
 * o fim do ciclo pago. A situação de cobrança corta o que está liberado agora.
 */
export default function DireitoDaAssinatura({licenca, versaoDetalhe, opcionais, modulos, ocupado, onContratar, onRetirar}) {
    const nivel = efeitoNoAcesso(licenca.status).nivel;
    const ativa = vigente(licenca);
    const naVersao = new Map((versaoDetalhe?.modulos || []).map((item) => [item.id_modulo, item]));
    const contratados = new Map((opcionais?.contratados || licenca.opcionais || []).map((item) => [item.id_modulo, item]));
    // Os módulos do sistema; sem eles, os da versão.
    const linhas = (modulos.length ? modulos : (versaoDetalhe?.modulos || []).map((item) => ({id: item.id_modulo, nome: item.modulo?.nome, funcionalidades: []})))
        .map((modulo) => ({modulo, naVersao: naVersao.get(modulo.id), contratado: contratados.get(modulo.id)}));
    const noDireito = linhas.filter(({naVersao: item, contratado}) => item?.situacao === 'INCLUIDO' || (item?.situacao === 'OPCIONAL' && contratado));
    const totalNoDireito = noDireito.reduce((total, {modulo}) => total + (modulo.funcionalidades || []).length, 0);
    const totalLiberado = noDireito.reduce((total, {modulo}) => total + liberadas(modulo.funcionalidades || [], nivel), 0);

    return (
        <Stack spacing={2}>
            {nivel === 'leitura' ? <Alert severity="warning"><b>Somente consulta.</b> O direito continua o mesmo, mas só as funcionalidades de leitura estão liberadas até a cobrança ser regularizada.</Alert> : null}
            {nivel === 'nenhum' ? <Alert severity="error"><b>Sem acesso.</b> {efeitoNoAcesso(licenca.status).texto}</Alert> : null}
            <Alert severity="info" icon={false}>
                <b>Direito = módulos incluídos na versão {licenca.versao?.numero} + opcionais contratados.</b> Contratar um opcional vale na hora, e o valor entra na fatura seguinte, sem pró-rata. Tirar fica para o fim do ciclo pago.
            </Alert>
            <Paper variant="outlined" component="section" aria-label="Módulos">
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{px: {xs: 2, md: 2.5}, pt: 2, pb: 1}} flexWrap="wrap" useFlexGap gap={1}>
                    <Typography variant="h6" component="h2" fontWeight={700}>Módulos</Typography>
                    <Typography variant="caption" color="text.secondary">{noDireito.length} de {linhas.length} no direito · {totalLiberado} de {totalNoDireito} funcionalidades liberadas</Typography>
                </Stack>
                <Box sx={{overflowX: 'auto'}}>
                    <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 520}}>
                        <thead>
                            <Box component="tr" sx={{'& th': {px: 1.5, py: 1, textAlign: 'left', color: 'text.secondary', fontWeight: 500, fontSize: 13}}}>
                                <th>Módulo</th>
                                <th>Na assinatura</th>
                                <th>Liberadas agora</th>
                                <Box component="th" sx={{textAlign: 'right !important'}}>Opcional</Box>
                            </Box>
                        </thead>
                        <tbody>
                            {linhas.map(({modulo, naVersao: item, contratado}) => {
                                const funcionalidades = modulo.funcionalidades || [];
                                const dentro = item?.situacao === 'INCLUIDO' || (item?.situacao === 'OPCIONAL' && contratado);
                                const saindo = contratado?.sai_apos;
                                const escrita = funcionalidades.filter((f) => f.tipo === 'ESCRITA').length;
                                let naAssinatura;
                                if (!item) naAssinatura = <Typography variant="body2" color="text.secondary">Fora do plano</Typography>;
                                else if (item.situacao === 'INCLUIDO') naAssinatura = <Chip size="small" color="primary" label="Incluído no plano" />;
                                else if (!ativa) naAssinatura = contratado ? <Chip size="small" color="info" label="Contratado" /> : <Typography variant="body2" color="text.secondary">Não contratado</Typography>;
                                else {
                                    naAssinatura = (
                                        <Box>
                                            <FormControlLabel
                                                control={<Switch size="small" checked={Boolean(contratado)} disabled={ocupado || Boolean(saindo)}
                                                                 onChange={(event) => (event.target.checked ? onContratar(modulo) : onRetirar(modulo))} />}
                                                label={contratado ? 'Contratado' : 'Não contratado'}
                                            />
                                            {saindo ? (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Sai em {dataBR(saindo)}.{' '}
                                                    <Button size="small" sx={{p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline'}} disabled={ocupado} onClick={() => onContratar(modulo, true)}>Manter</Button>
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    );
                                }
                                return (
                                    <Box component="tr" key={modulo.id} sx={dentro ? undefined : {'& td': {color: 'text.secondary'}}}>
                                        <Box component="td" sx={celula}>
                                            <Typography variant="body2" fontWeight={600} color={dentro ? 'text.primary' : 'text.secondary'}>{modulo.nome}</Typography>
                                            <Typography variant="caption" color="text.secondary">{funcionalidades.length} funcionalidades · {escrita} de escrita</Typography>
                                        </Box>
                                        <Box component="td" sx={celula}>{naAssinatura}</Box>
                                        <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>
                                            {dentro ? (
                                                <>
                                                    {liberadas(funcionalidades, nivel)} de {funcionalidades.length}
                                                    {liberadas(funcionalidades, nivel) < funcionalidades.length ? <Chip size="small" color="warning" variant="outlined" sx={{ml: 1}} label={nivel === 'leitura' ? 'só leitura' : 'sem acesso'} /> : null}
                                                </>
                                            ) : '—'}
                                        </Box>
                                        <Box component="td" sx={{...celula, textAlign: 'right', whiteSpace: 'nowrap'}}>
                                            {item?.situacao === 'OPCIONAL' ? `+ ${formatReais(item.preco_opcional)}` : '—'}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            </Paper>
        </Stack>
    );
}
